import React, { ReactNode, useMemo, useRef } from 'react'
import { PopoverProps } from 'core/components/dialogs/Popover'
import Button from 'core/components/buttons/Button'
import cn from 'utils/cn'
import Tooltip from 'core/components/dialogs/Tooltip'
import Icon from 'core/components/icons/Icon'

interface Props extends Omit<PopoverProps, 'show' | 'content'> {
  /**
   * Title text or element displayed in the confirmation popover.
   */
  title?: string | React.ReactElement
  /**
   * Text or element for the confirm button.
   */
  confirmButton?: string | React.ReactElement
  /**
   * Text or element for the cancel button.
   */
  cancelButton?: string | React.ReactElement
  /**
   * Optional icon to display in the popover.
   */
  icon?: ReactNode | null
  /**
   * Additional class names for the popover container.
   */
  className?: string
  /**
   * If true, disables the popover and its actions.
   */
  disabled?: boolean
  /**
   * Callback fired when the confirm button is clicked.
   */
  onConfirm?: VoidFunction
  /**
   * Disables the confirm button, e.g. while the confirmed action is in flight.
   * No effect on a custom `confirmButton` element.
   */
  confirmDisabled?: boolean
  /**
   * Position of the popover relative to the reference element.
   */
  // Re-use position options from PopoverProps to add proper documentation for storybook
  position?:
    | 'top-left'
    | 'top-middle'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-middle'
    | 'bottom-right'
    | 'left-top'
    | 'left-middle'
    | 'left-bottom'
    | 'right-top'
    | 'right-middle'
    | 'right-bottom'
}

const PopConfirm = ({
  title = 'Are you sure?',
  confirmButton = 'Yes',
  cancelButton = 'No',
  position = 'left-middle',
  icon = null,
  className,
  onConfirm = console.log,
  disabled = false,
  confirmDisabled = false,
  children,
  ...props
}: Props) => {
  // An already-open popover may not repaint `disabled` before the next click
  // lands, so the click itself checks the latest value through a ref.
  const confirmDisabledRef = useRef(confirmDisabled)
  confirmDisabledRef.current = confirmDisabled

  const content = useMemo(() => {
    return (
      <div className={cn(['bg-white rounded-md py-4 px-4 shadow', className])}>
        <div className="flex items-center gap-3 mb-2">
          <span>
            {icon ?? (
              <Icon
                name="exclamation-mark"
                size={9}
                className="text-white bg-warning-2 p-1 rounded-full mr-4"
              />
            )}
          </span>
          {title}
        </div>
        <div className="flex items-center justify-end gap-3 w-full">
          {typeof cancelButton === 'string' ? (
            <Button variant="outline" className="text-sm px-3!">
              {cancelButton}
            </Button>
          ) : (
            cancelButton
          )}

          {typeof confirmButton === 'string' ? (
            <Button
              variant="primary"
              className="text-sm px-3!"
              onClick={() => {
                if (confirmDisabledRef.current) return
                onConfirm()
              }}
              disabled={confirmDisabled}
            >
              {confirmButton}
            </Button>
          ) : (
            <div onClick={onConfirm}>{confirmButton}</div>
          )}
        </div>
      </div>
    )
  }, [
    className,
    icon,
    title,
    cancelButton,
    confirmButton,
    onConfirm,
    confirmDisabled
  ])

  return (
    <Tooltip
      dismissOnInteraction
      content={content}
      position={position}
      disabled={disabled}
      wrapperClassName="leading-normal"
      {...props}
      triggerMode="click"
    >
      {children}
    </Tooltip>
  )
}

export default PopConfirm
